/* -----------------Icons--------------- */
import { isMockedData } from "@constants/demo";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { getNormalizedScore } from "@features/session/helpers/aiFeedback";
import type { EmotionData } from "@features/session/hooks/useAIFeedbackStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useColorsFromMastery } from "@hooks/useColorsFromMasteryLevels";
import type { Confidence } from "@memorang/types";
import type {
	OriginalItemTypes,
	OriginalStimulusTypes,
	RenderableItemTypes,
	SubskillAssessment,
	SubskillSchema,
} from "@memorang/types/src/session/new-session";
import {
	Box,
	CustomChip,
	DonutChart,
	type IconType,
	OverlineText,
} from "@memorang/ui";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import EmotionScoreDisplay from "../EmotionScoreDisplay";

type Props = {
	confidence?: Confidence;
	numAnsweredStat?: string;
	correct?: boolean;
	pointsEarnedStatValue?: string;
	score?: number;
	subskillAssessments?: SubskillAssessment[];
	totalScore: number;
	itemType?: RenderableItemTypes;
	originalItemType?: OriginalItemTypes;
	stimulusType?: OriginalStimulusTypes;
	subskillsSchema?: SubskillSchema[];
	topFiveEmotions?: EmotionData[];
};

const getSubskillLabels = (
	itemType: RenderableItemTypes | undefined,
): SubskillSchema[] => {
	if (itemType === "AUDIO_INPUT" || itemType === "AUDIO_INPUT_EMOTION") {
		return [
			{
				name: "session.subskills.delivery",
				icon: "microphone-outline",
			},
			{ name: "session.subskills.languageUse", icon: "translate" },
			{
				name: "session.subskills.topicDevelopment",
				icon: "comment-text-outline",
			},
		];
	}

	return [
		{
			name: "session.subskills.content",
			icon: "file-document-outline",
		},
		{ name: "session.subskills.languageUse", icon: "translate" },
		{
			name: "session.subskills.organizationAndDevelopment",
			icon: "text-box-outline",
		},
	];
};

const getSubskillDescriptionLabel = (
	originalItemType: OriginalItemTypes | undefined,
	stimulusType: OriginalStimulusTypes | undefined,
) => {
	if (stimulusType === "INDEPENDENT_SPEAKING_STIMULUS") {
		return "independentSpeaking";
	}
	if (originalItemType === "AUDIO_INPUT") {
		return "integratedSpeaking";
	}
	if (originalItemType === "ACADEMIC_DISCUSSION_RESPONSE") {
		return "academicDiscussionWriting";
	}
	if (originalItemType === "INTEGRATED_WRITING_RESPONSE") {
		return "integratedWriting";
	}

	return null;
};

const ScoreItem = ({
	label,
	score,
	icon,
	backgroundColor,
	color,
	description,
}: {
	label: string;
	score: number | string;
	icon: IconType;
	backgroundColor: string;
	color: string;
	description: string | null;
}) => {
	const theme = useAppTheme();
	const [showInfoDialog, setShowInfoDialog] = useState(false);

	const openInfoDialog = () => {
		setShowInfoDialog(true);
	};

	return (
		<>
			<Pressable
				style={{
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
				}}
				onPress={openInfoDialog}
			>
				<Box flexDirection="row" alignItems="center" gap={16} flex={1}>
					<Icon name={icon} size={18} color={theme.colors.text} />
					<Text
						variant="bodyLarge"
						numberOfLines={1}
						style={{
							flex: 1,
							paddingRight: 16,
						}}
					>
						{label}
					</Text>
				</Box>
				<CustomChip
					style={{ backgroundColor }}
					textStyle={{ color }}
					onPress={openInfoDialog}
				>
					{score}
				</CustomChip>
			</Pressable>
			<ConfirmDialog
				dismissCallback={() => setShowInfoDialog(false)}
				body={description ?? ""}
				icon="information-outline"
				title={label}
				visible={showInfoDialog}
				confirmCta="Close"
				confirmCallback={() => setShowInfoDialog(false)}
			/>
		</>
	);
};

const AiPostAnswerHeader = (props: Props) => {
	const {
		score,
		subskillAssessments,
		totalScore,
		itemType,
		originalItemType,
		stimulusType,
		subskillsSchema,
		topFiveEmotions: topFiveEmotionsFromProps,
	} = props;
	const theme = useAppTheme();
	const colors = useColorsFromMastery();
	const { t } = useTranslation();

	const getChipColor = useCallback(
		(score: number | undefined, maxScore: number) => {
			const { none: empty } = theme.colors.progress;
			if (score === undefined) {
				return { backgroundColor: empty, color: theme.colors.text };
			}
			const normalizedScore = getNormalizedScore(score, maxScore);

			if (normalizedScore === undefined) {
				return { backgroundColor: empty, color: theme.colors.text };
			}
			if (normalizedScore < 40) {
				return { backgroundColor: colors.basic, color: "white" };
			}
			if (normalizedScore < 60) {
				return { backgroundColor: colors.lowIntermediate, color: "white" };
			}
			if (normalizedScore < 80) {
				return { backgroundColor: colors.highIntermediate, color: "white" };
			}
			return { backgroundColor: colors.advanced, color: "white" };
		},
		[theme.colors.progress, theme.colors.text, colors],
	);

	const subskills = useMemo(() => {
		const subskills = subskillsSchema
			? subskillsSchema
			: getSubskillLabels(itemType);
		const subskillScores = new Map(
			subskillAssessments?.map((subskill) => [
				subskill.name?.toLowerCase(),
				subskill.score,
			]),
		);

		return subskills.map((subskill) => {
			const { name, icon } = subskill;
			const title = isMockedData ? name : `${name}.title`;
			const titleText = title.toLowerCase();
			const subskillScore = subskillScores.get(titleText);
			const label = t(title);
			const maxScore = subskill.maxScore ?? totalScore;
			const chipColor = getChipColor(subskillScore, maxScore);
			const description = getSubskillDescriptionLabel(
				originalItemType,
				stimulusType,
			);

			return (
				<ScoreItem
					key={label}
					label={label}
					score={`${subskillScore ?? "--"} / ${maxScore}`}
					icon={icon}
					backgroundColor={chipColor.backgroundColor}
					color={chipColor.color}
					description={
						subskill.description
							? subskill.description
							: description
								? t(`${name}.${description}`)
								: null
					}
				/>
			);
		});
	}, [
		subskillsSchema,
		itemType,
		subskillAssessments,
		t,
		getChipColor,
		originalItemType,
		stimulusType,
		totalScore,
	]);

	const topFiveEmotions = useMemo(() => {
		return topFiveEmotionsFromProps ?? [];
	}, [topFiveEmotionsFromProps]);

	const transformedEmotions = useMemo(() => {
		return [
			{
				emotions: topFiveEmotions.map((emotion) => ({
					name: emotion.name,
					score: emotion.score,
				})),
			},
		];
	}, [topFiveEmotions]);

	return (
		<Box gap={16} marginTop={16}>
			<Box alignItems="center" justifyContent="center">
				<DonutChart
					skeleton={score === undefined}
					distribution={[score ?? 0, totalScore - (score ?? 0)]}
					innerRadius={52}
					variant="progress"
					radius={64}
					score={score ? (score / totalScore) * 100 : undefined}
				>
					<Box alignItems="center" justifyContent="center">
						<Text variant="titleLarge">
							{score === undefined
								? `-- / ${totalScore}`
								: `${score} / ${totalScore}`}
						</Text>
						<OverlineText>{t("session.overall")}</OverlineText>
					</Box>
				</DonutChart>
			</Box>
			{subskills}
			{itemType === "AUDIO_INPUT_EMOTION" && (
				<Box>
					<Box flexDirection="row" alignItems="center" gap={16}>
						<Icon
							name="emoticon-happy-outline"
							size={18}
							color={theme.colors.text}
						/>
						<Text variant="bodyLarge">Emotions</Text>
					</Box>
					<EmotionScoreDisplay predictions={transformedEmotions} />
				</Box>
			)}
		</Box>
	);
};

export default AiPostAnswerHeader;
